import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GetCustomerAction } from './get-customer.action';
import { FilterModel } from '../../../data/model/filteModel/filter-model';
import { FilterConstants } from '../../../shared/constants/filter-constants';
import { ApiResponseModel } from '../../../data/model/ApiResponseModel/api-response.model';
import { CustomerV2Model } from '../../../data/model/CustomerV2Model/customer-v2.model';
import { DataManagerService } from '../../../data/services/DataManager/data-manager.service';
import { AppConstants } from '../../../shared/constants/app-constants';
import { PermissionConstants } from '../../../shared/constants/permission-constants';
import { AlertMessageDialogComponent } from '../../../dialogs/alert-message-dialog/alert-message-dialog.component';
import { ErrorConstants } from '../../../shared/constants/error-constants';
import { ExportModel } from '../../../data/model/ExportModel/export-model';
import { ExportConstants } from '../../../shared/constants/export-constants';
import { Highlight } from '../../../shared/highlights/highlight';
import { ariaSort, SortDirection } from '../../../ui/table/payarc-sort-icon.component';
import { PagerPage } from '../../../ui/pager/payarc-pager.component';

@Component({
    selector: 'app-customer-list',
    templateUrl: './customer-list.component.html',
    styleUrls: ['./customer-list.component.css'],
    standalone: false
})
export class CustomerListComponent extends GetCustomerAction implements OnInit {
  filter: FilterModel[] = FilterConstants.customerListFilter;
  customerList: ApiResponseModel<CustomerV2Model>;
  exportExcel: ExportModel[] = ExportConstants.customerListExport;
  exportData = {exportServiceName: AppConstants.SERVICE_NAME_EXPORT_DASHBOARD + '/customer', redirectUrl: '/customer/customer-list', fileName: 'Payarc_Customer_Report', heading: 'Customers'};
  showExport = false;
  _service_name = '/customers';
  excel = 1;
  blob: any;
  to_date: any;
  from_date: any;
  today: Date = new Date();
  isPaypalMerchant = false;
  /** Distinguishes a genuine empty result from a failed request, so the table doesn't
   * claim "No customers found" when the list actually failed to load. */
  hasLoadError = false;

  constructor(
    router: Router,
    private dialog: MatDialog,
    route: ActivatedRoute,
    dataManager: DataManagerService,
    snackBar: MatSnackBar,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject('LOCALSTORAGE') private localStorage: any, private datePipe: DatePipe
  ) {
    super();
    this.snackBar = snackBar;
    this.alertDialog = dialog;
    this.errorMsg = null;
    this.searchHits = new Subject<void>();
    this.dataManager = dataManager;
    this.filter_storage_key = FilterConstants.CUSTOMER_FILTER_STORAGE_KEY;
    this.count_storage_key = FilterConstants.CUSTOMER_FILTER_COUNT_STORAGE_KEY;
    this.router = router;
    this.route = route;
    this.redirect_link = '/customer/customer-details';
    this.customerList = new ApiResponseModel();
    this.getListUrl = AppConstants.BASE_URL + this._service_name;
    this.isPaypalMerchant = this.isPaypalAccount();
    if (this.isPaypalMerchant) {
      // Saved PayPal accounts, which can be charged in place of a card.
      this.includes = '&include=paypal_vault_token';
    }
    this.hasAdvancefilter = true;
    this.create_permission = this.permissionCheck(PermissionConstants.PERMISSION_CREATE_CUSTOMER);
    this.filterSubscription = this.dataManager.filterUrl.subscribe(
      (url) => {
        this.filterPostFix = url;
        this.dataManager.prev_url = url;
        this.is_filter = true;
        this.setUrl();
      },
      (err) => console.log(err)
    );
  }

  ngOnInit() {
    this.searchHits.pipe(debounceTime(600)).subscribe(() => this.setUrl(1));
    if (isPlatformBrowser(this.platformId)) {
      if (!this.localStorage.getItem(this.filter_storage_key) && !this.localStorage.getItem(this.count_storage_key)) {
        this.setUrl();
      }
    }
  }

  addNew() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.CUSTOMER_DATA);
    }
    this.router.navigate(['/customer/create-customer']);
  }

  onSearch(value: string) {
    this.searchText = value;
    this.searchHits.next();
  }

  /** Overrides the base no-arg `setUrl()` with an explicit page, so the pager can request
   * any page without the base implementation's implicit reset to page 1. Also keeps
   * `_getcustomerexcel` (the Export flow's URL) in lockstep with whatever filters/search/
   * sort/date-range produced the list currently on screen. */
  setUrl(page = 1): string {
    let url = this.getListUrl;
    url += isNaN(this.limit) ? '?limit=0' : '?limit=' + this.limit + '&page=' + page;
    if (!this.isNullOrUndefined(this.searchText)) {
      url += '&search=' + this.searchText;
    }
    if (!this.isNullOrUndefined(this.orderBy)) {
      url += '&orderBy=' + this.orderBy + '&sortedBy=' + this.sortBy;
    }
    if (this.is_filter && this.filterPostFix) {
      url += this.filterPostFix;
    } else if (this.hasAdvancefilter && !this.isNullOrUndefined(this.filterPostFix)) {
      url += this.filterPostFix;
    }
    if (!this.isNullOrUndefined(this.includes)) {
      url += this.includes;
    }
    if (!this.isNullOrUndefined(this.from)) {
      url += '&from_date=' + this.from;
    }
    if (!this.isNullOrUndefined(this.to)) {
      url += '&to_date=' + this.to;
    }
    this.limitSelected = true;
    this.getList(url);
    this._getcustomerexcel = url + '&excel=' + this.excel;
    return url;
  }

  clearDates() {
    this.from_date = null;
    this.to_date = null;
    this.datereport();
  }

  datereport() {
    if ((this.from_date && this.from_date.toString() === 'Invalid Date') || (this.to_date && this.to_date.toString() === 'Invalid Date')) {
      this.showSnackBar('Enter valid date.', 'Ok', 3000);
    } else if (this.isNullOrUndefined(this.from_date) || this.isNullOrUndefined(this.to_date)) {
      if (!this.isNullOrUndefined(this.searchText) || (this.isNull(this.from_date) && this.isNull(this.to_date))) {
        this.from = this.from_date;
        this.to = this.to_date;
        this.setUrl(1);
      } else {
        return;
      }
    } else if (this.from_date > this.to_date) {
      this.showSnackBar('From date should not exceed ' + this.datePipe.transform(this.to_date, 'MM/dd/yyyy') + '.', 'Ok', 3000);
    } else {
      this.from = this.isNullOrUndefined(this.from_date) ? '' : this.datePipe.transform(this.from_date, 'yyyy-MM-dd');
      this.to = this.isNullOrUndefined(this.to_date) ? '' : this.datePipe.transform(this.to_date, 'yyyy-MM-dd');
      this.setUrl(1);
    }
  }

  checkdate() {
    if (!this.isNull(this.from_date) && this.from_date.toString() === 'Invalid Date') {
      this.datereport();
    }
  }

  checkdate2() {
    if (!this.isNull(this.to_date) && this.to_date.toString() === 'Invalid Date') {
      this.datereport();
    }
  }

  limitChange() {
    this.setUrl(1);
  }

  get currentPage(): number {
    return this.customerList?.meta?.pagination?.current_page || 1;
  }

  get totalPages(): number {
    const total = this.customerList?.meta?.pagination?.total || 0;
    return this.limit > 0 ? Math.max(1, Math.ceil(total / this.limit)) : 1;
  }

  get pagerRangeLabel(): string {
    const pagination = this.customerList?.meta?.pagination;
    if (!pagination?.total) {
      return '';
    }
    const start = 1 + (pagination.current_page - 1) * this.limit;
    const end = pagination.current_page === 1 ? pagination.count : start + pagination.count - 1;
    return `Showing ${start}–${end} of ${pagination.total}`;
  }

  get pagerPages(): PagerPage[] {
    const total = this.totalPages;
    const current = this.currentPage;
    if (total <= 7) {
      return Array.from({length: total}, (_, i) => i + 1);
    }
    const pages = new Set<number>([1, total, current, current - 1, current + 1]);
    const sorted = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
    const out: PagerPage[] = [];
    let prev = 0;
    for (const p of sorted) {
      if (prev && p - prev > 1) {
        out.push('...');
      }
      out.push(p);
      prev = p;
    }
    return out;
  }

  pagerPrev() {
    this.setUrl(Math.max(1, this.currentPage - 1));
  }

  pagerNext() {
    this.setUrl(Math.min(this.totalPages, this.currentPage + 1));
  }

  pagerSelect(page: number) {
    this.setUrl(page);
  }

  sortDirection(key: string): SortDirection {
    if (this.orderBy !== key) { return null; }
    return this.sortBy === 'asc' ? 'asc' : 'desc';
  }

  ariaSort = ariaSort;

  setSorting(orderBy) {
    if (orderBy === this.orderBy) {
      this.sortBy = this.sortBy === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderBy = orderBy;
      this.sortBy = 'asc';
    }
    this.setUrl(1);
  }

  /**
   * func to add charge
   * for selected customer
   * @param customer selected customer object
   */
  chargeCustomer(customer: CustomerV2Model) {
    const data = {};
    if (!this.permissionCheck(PermissionConstants.PERMISSION_CREATE_PAYMENT)) {
      data['head'] = 'Alert';
      data['message'] = PermissionConstants.PERMISSIONS_CREATE_MESSAGE_PAYMENT;
      this.dialog.open(AlertMessageDialogComponent, { data: data , panelClass: 'pa-shell-dialog' });
    } else if (customer.card.data.length <= 0 && !(customer.paypal_vault_token?.data?.length > 0)) {
      data['head'] = 'Alert';
      data['message'] = 'This customer doesn\'t have any cards to charge on';
      this.dialog.open(AlertMessageDialogComponent, { data: data , panelClass: 'pa-shell-dialog' });
    } else {
      const data = {
                customer_id: customer.customer_id ? customer.customer_id : null,
                customer_email: customer.email ? customer.email : null,
                customer_phone: customer.phone ? customer.phone : null,
                source: customer.source_id ? customer.source_id : null,
                sourceList: customer.card.data ? customer.card.data : null,
                paypalSourceList: customer.paypal_vault_token?.data ?? [],
                customer_name: customer.name ? customer.name: null
              }
      localStorage.setItem(this.CUSTOMER_DATA, JSON.stringify(data));
      this.router.navigate(['../capture-payment'], {queryParams: {id: data.customer_id}, relativeTo: this.route})
    }
  }

  nullToDoubleMinus(value) {
    return value ? value : '--';
  }

  /** Keyboard equivalent of the row's [routerLink] click — RouterLink on a non-anchor
   * element only binds a click listener, so Enter needs its own navigation call. */
  openCustomerDetails(customer: CustomerV2Model) {
    this.router.navigate([this.redirect_link], {queryParams: {id: customer.customer_id}});
  }

  highlight(value) {
    return Highlight.highlight(value, this.searchText);
  }

  protected getListRes(customerResponse: ApiResponseModel<CustomerV2Model>) {
    this.customerList = customerResponse;
    this.metadata = customerResponse.meta.pagination;
    this.hasLoadError = false;
    if (customerResponse.data.length === 0) {
      this.errorMsg = ErrorConstants.customerErrorMsg[0];
    } else {
      this.errorMsg = null;
    }
    for (const customer of this.customerList.data) {
      for (const card of customer.card.data) {
        if (card.is_default) {
          customer.defaultCard = card;
        }
      }
    }
  }

  protected getListErr(error: any) {
    this.hasLoadError = true;
    this.errorMsg = this.networkErrorHandler(error, ErrorConstants.customerErrorMsg);
  }

  exportexcel() {
    this.showExport = true;
  }

  protected passCustomerExcelResponse(res: any) {
    this.blob = new Blob([res], { type: 'application/vnd.ms-excel' });
    const downloadURL = window.URL.createObjectURL(res);
    const link = document.createElement('a');
    link.href = downloadURL;
    link.download = 'Payarc_Customer_Report.xls';
    link.click();
  }

  protected passCustomerExcelError(err: any) {
  }
}
